import { useState, useEffect, ReactNode } from 'react';
import { reviews as initialReviews, Review } from '../mocks/reviews';
import { supabaseClient, shouldUseSupabase } from '../lib/supabaseClient';

export interface ReviewContextType {
  allReviews: Review[];
  getProductReviews: (productId: number) => Review[];
  getProductRating: (productId: number) => { avg: number; count: number };
  addReview: (review: Omit<Review, 'id' | 'helpful'>) => void;
  markHelpful: (reviewId: number) => void;
}

import { ReviewContext } from './ReviewContextValue';

export const ReviewProvider = ({ children }: { children: ReactNode }) => {
  const [allReviews, setAllReviews] = useState<Review[]>(initialReviews);

  // Cargar reseñas desde Supabase si está disponible
  useEffect(() => {
    const loadReviews = async () => {
      if (!shouldUseSupabase() || !supabaseClient) return;
      try {
        const { data, error } = await supabaseClient
          .from('reviews')
          .select('id, product_id, author, avatar, rating, title, comment, date, verified, helpful')
          .order('date', { ascending: false });

        if (error || !data || data.length === 0) return;

        const mapped: Review[] = data.map((r: any) => ({
          id: r.id,
          productId: r.product_id,
          author: r.author || 'Cliente verificado',
          avatar: r.avatar || (r.author || 'C').substring(0, 2).toUpperCase(),
          rating: r.rating,
          date: r.date || '',
          title: r.title || '',
          comment: r.comment || '',
          verified: r.verified ?? false,
          helpful: r.helpful ?? 0,
        }));

        setAllReviews(mapped);
      } catch {
        // Silently keep mock data on error
      }
    };

    loadReviews();
  }, []);

  const getProductReviews = (productId: number): Review[] => {
    return allReviews.filter((r) => r.productId === productId);
  };

  const getProductRating = (productId: number) => {
    const productReviews = allReviews.filter((r) => r.productId === productId);
    if (productReviews.length === 0) return { avg: 0, count: 0 };
    const avg =
      productReviews.reduce((sum, r) => sum + r.rating, 0) /
      productReviews.length;
    return { avg, count: productReviews.length };
  };

  const addReview = (review: Omit<Review, 'id' | 'helpful'>) => {
    const newReview: Review = {
      ...review,
      id: Date.now(),
      helpful: 0,
    };
    setAllReviews((prev) => [newReview, ...prev]);
  };

  const markHelpful = (reviewId: number) => {
    setAllReviews((prev) =>
      prev.map((r) =>
        r.id === reviewId ? { ...r, helpful: r.helpful + 1 } : r
      )
    );
  };

  return (
    <ReviewContext.Provider
      value={{ allReviews, getProductReviews, getProductRating, addReview, markHelpful }}
    >
      {children}
    </ReviewContext.Provider>
  );
};
